import React from 'react';
import { Facebook, Linkedin, Instagram, Youtube, MapPin, Phone, Mail, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SEOLink } from '../seo/SEOLink';

// X (Twitter) Icon Component
const XIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Pinterest Icon Component  
const PinterestIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
  </svg>
);

// Social media links from zarooriretail.com (verified)
const socialLinks = [
  { Icon: Facebook, href: 'https://www.facebook.com/zarooriretailpremium', label: 'Facebook' },
  { Icon: Instagram, href: 'https://www.instagram.com/zarooriretail', label: 'Instagram' },
  { Icon: XIcon, href: 'https://x.com/zarooriretail', label: 'X' },
  { Icon: Linkedin, href: 'https://www.linkedin.com/company/100707300/', label: 'LinkedIn' },
  { Icon: Youtube, href: 'https://www.youtube.com/@ZarooriRetailYT', label: 'YouTube' },
  { Icon: PinterestIcon, href: 'https://in.pinterest.com/Zarooriretail/', label: 'Pinterest' },
];

const Footer = () => {
  return (
    <footer className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')]" />
      
      {/* Decorative Orb */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#ef4444]/10 rounded-full blur-3xl" />
      
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <SEOLink to="/" className="flex items-center gap-3">
              <div className="bg-white rounded-xl p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_49e3f0dc-64cb-41b7-891b-65258ccb235b/artifacts/4zp8z6bt_ZR%20landscape%20logo.png"
                  alt="Zaroori Retail - Premium, Prolific, Presence"
                  className="h-14 w-auto object-contain"
                  width="160"
                  height="56"
                  loading="lazy" decoding="async"
                />
              </div>
            </SEOLink>
            <p className="text-slate-400 text-sm leading-relaxed">
              India's #1 marketplace onboarding service. We help brands get listed and grow on premium online marketplaces.
            </p>
            <div className="flex gap-3">
              {socialLinks.map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 glass rounded-xl flex items-center justify-center text-white hover:bg-gradient-to-br hover:from-[#ef4444] hover:to-[#dc2626] transition-all hover:scale-110"
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-bold text-white mb-6">Products</h3>
            <ul className="space-y-3">
              {[
                { name: 'Zaroori Jini OMS', path: '/zaroori-jini-oms' },
                { name: 'OMS Features',     path: '/zaroori-jini-oms/features' },
                { name: 'OMS Pricing',      path: '/zaroori-jini-oms/pricing' },
                { name: 'OMS Integrations', path: '/zaroori-jini-oms/integrations' },
                { name: 'OMS AI',           path: '/zaroori-jini-oms/ai' },
                { name: 'OMS API',          path: '/zaroori-jini-oms/api' },
                { name: 'OMS Security',     path: '/zaroori-jini-oms/security' },
                { name: 'Book a Demo',      path: '/zaroori-jini-oms/demo' },
              ].map((link) => (
                <li key={link.name}>
                  <SEOLink to={link.path} className="text-slate-400 hover:text-white hover:pl-2 transition-all flex items-center gap-2 group text-sm">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#ef4444]" />
                    {link.name}
                  </SEOLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Our Team', path: '/team' },
                { name: 'Services', path: '/services' },
                { name: 'Success Stories', path: '/success-stories' },
                { name: 'Blog', path: '/blog' },
                { name: 'Resources', path: '/resources' },
                { name: 'Contact', path: '/contact' },
                { name: 'Sitemap', path: '/sitemap' }
              ].map((link) => (
                <li key={link.name}>
                  <SEOLink 
                    to={link.path}
                    className="text-slate-400 hover:text-white hover:pl-2 transition-all flex items-center gap-2 group text-sm"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#ef4444]" />
                    {link.name}
                  </SEOLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Marketplace Guides */}
          <div>
            <h3 className="text-lg font-bold text-white mb-6">Marketplace Guides</h3>
            <ul className="space-y-3">
              {[
                { name: 'Global Marketplace Hub', path: '/global-marketplace-seller-guides' },
                { name: 'Myntra Onboarding', path: '/myntra-seller-onboarding' },
                { name: 'Nykaa Onboarding', path: '/nykaa-seller-onboarding' },
                { name: 'AJIO Onboarding', path: '/ajio-seller-onboarding' },
                { name: 'Myntra Account Mgmt', path: '/myntra-seller-account-management' },
                { name: 'Amazon India', path: '/amazon-seller-onboarding-india' },
                { name: 'Sell on India', path: '/sell-on-india-marketplaces' },
                { name: 'Sell on UAE', path: '/sell-on-uae-marketplaces' },
                { name: 'Sell on US', path: '/sell-on-us-marketplaces' }
              ].map((link) => (
                <li key={link.name}>
                  <SEOLink 
                    to={link.path}
                    className="text-slate-400 hover:text-white hover:pl-2 transition-all flex items-center gap-2 group text-sm"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#ef4444]" />
                    {link.name}
                  </SEOLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold text-white mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 glass rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#ef4444]" />
                </div>
                <span className="text-slate-400 text-sm">Ludhiana | Noida | Dubai | USA</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 glass rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-[#ef4444]" />
                </div>
                <a href="tel:+917743005242" className="text-slate-400 hover:text-white transition-colors text-sm">
                  +91 77430 05242
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 glass rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#ef4444]" />
                </div>
                <a href="mailto:business@zarooriretail.com" className="text-slate-400 hover:text-white transition-colors text-sm">
                  business@zarooriretail.com
                </a>
              </li>
            </ul>

            {/* Newsletter */}
            <div className="mt-6">
              <h5 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#ef4444]" />
                Subscribe to Newsletter
              </h5>
              <div className="flex gap-2">
                <Input 
                  type="email" 
                  placeholder="Your email" 
                  className="glass border-0 text-white placeholder:text-slate-500 rounded-xl focus:ring-2 focus:ring-[#ef4444]"
                />
                <Button className="bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c] rounded-xl px-4">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marketplace Account Management & Growth Services — Sprint 1 mega-link block (Feb 2026) */}
      <div className="bg-slate-950/40 border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h3 className="text-lg font-bold text-white mb-1">Marketplace Account Management & Growth Services</h3>
          <p className="text-slate-400 text-sm mb-5">
            End-to-end operations and growth for Myntra, Nykaa, AJIO and 30+ marketplaces.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
            {[
              { name: 'Account Management Hub', path: '/marketplace-account-management-services' },
              { name: 'Myntra Account Management', path: '/myntra-seller-account-management' },
              { name: 'Myntra Cataloging Services', path: '/myntra-cataloging-services' },
              { name: 'Myntra Photoshoot Services', path: '/myntra-product-photoshoot-services' },
              { name: 'Myntra PLA Advertising', path: '/myntra-pla-advertising-services' },
              { name: 'Myntra Listing Optimization', path: '/myntra-listing-optimization-services' },
              { name: 'Myntra Return Management', path: '/myntra-return-management-services' },
              { name: 'Myntra Reconciliation', path: '/myntra-payment-reconciliation-services' },
              { name: 'Myntra Sales Growth', path: '/myntra-sales-growth-services' },
              { name: 'Myntra Brand Approval', path: '/myntra-brand-approval-india' },
              { name: 'Nykaa Onboarding', path: '/nykaa-seller-onboarding' },
              { name: 'AJIO Onboarding', path: '/ajio-seller-onboarding' },
            ].map((link) => (
              <SEOLink
                key={link.path}
                to={link.path}
                className="text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-2 group"
                data-testid={`footer-megalink-${link.path.replace(/\//g, '')}`}
              >
                <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#ef4444]" />
                <span>{link.name}</span>
              </SEOLink>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 Zaroori Retail. All rights reserved. Made by <a href="https://zarooriinfortech.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#ef4444] transition-colors">ZarooriInfoTech</a>
          </p>
          <div className="flex gap-6 text-sm">
            <SEOLink to="/privacy-policy" className="text-slate-500 hover:text-white transition-colors">Privacy Policy</SEOLink>
            <SEOLink to="/terms-of-service" className="text-slate-500 hover:text-white transition-colors">Terms of Service</SEOLink>
            <SEOLink to="/refund-policy" className="text-slate-500 hover:text-white transition-colors">Refund Policy</SEOLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
